import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "@/components/icons";
import api, { errMsg } from "@/lib/api";
import { Loading, ErrorState, PageHeader } from "@/components/common";
import Voltar from "@/components/Voltar";
import StickyHScrollbar from "@/components/StickyHScrollbar";

function brl(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v || 0));
}
function fmtDataBR(iso) {
  if (!iso) return "";
  // Aceita `YYYY-MM-DD` e `YYYY-MM-DDTHH:mm:ss`.
  const raw = String(iso).slice(0, 10);
  const [y, m, d] = raw.split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

const PERIODO_LABEL = {
  mes_atual: "Mês atual",
  mes_anterior: "Mês anterior",
  trimestre: "Trimestre",
  ano: "Ano",
};

export default function FaturamentoVendedor() {
  const nav = useNavigate();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const periodo = sp.get("periodo") || "mes_atual";
  const nomeSp = sp.get("nome") || "";

  const [dados, setDados] = useState(null);
  const [clientes, setClientes] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const scrollerRef = useRef(null);

  const load = async () => {
    setCarregando(true); setErro(null);
    try {
      // 1) Painel de faturamento no período — pra pegar as datas de/ate
      const { data: painel } = await api.get(`/sankhya/faturamento/painel?periodo=${periodo}`);
      setDados(painel);
      // 2) Clientes do vendedor no mesmo intervalo
      const de = painel?.periodo?.de;
      const ate = painel?.periodo?.ate;
      const { data: cli } = await api.get(`/sankhya/faturamento/clientes?de=${de}&ate=${ate}&user_id=${id}`);
      setClientes(cli.itens || []);
    } catch (e) { setErro(errMsg(e)); }
    finally { setCarregando(false); }
  };
  useEffect(() => { load(); }, [id, periodo]);

  const voltar = () => nav(`/faturamento?periodo=${periodo}`);

  // Nome preferido: query string (do link) → cai pro por_pessoa se não veio.
  const nomeVend = useMemo(() => {
    if (nomeSp) return nomeSp;
    const p = (dados?.por_pessoa || []).find((x) => x.user_id === id);
    return p?.nome || "Vendedor";
  }, [dados, id, nomeSp]);

  // Só mostra coluna "Última" se algum cliente tiver a data preenchida.
  const temUltima = useMemo(
    () => (clientes || []).some((c) => !!c.ultima_compra_iso),
    [clientes]
  );

  if (carregando) return <Loading />;
  if (erro) return <ErrorState onRetry={load} message={erro} />;

  const de = fmtDataBR(dados?.periodo?.de);
  const ate = fmtDataBR(dados?.periodo?.ate);
  const totalLiquido = (clientes || []).reduce((acc, c) => acc + Number(c.liquido || 0), 0);

  return (
    <div className="pb-24 md:pb-6">
      <div className="mb-3">
        <Voltar fallback="/faturamento" />
      </div>
      <PageHeader
        title={`Clientes de ${nomeVend}`}
        subtitle={`${PERIODO_LABEL[periodo] || periodo} · ${de} a ${ate}`}
      />

      <div className="mt-4 rounded-2xl border border-black/5 bg-card shadow-[0_2px_10px_rgb(0,0,0,0.03)]">
        <div ref={scrollerRef} className="overflow-x-auto" data-testid="fatvend-tabela-wrap">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-black/5">
                <th className="text-left px-4 py-3 w-32">Cód. parceiro</th>
                <th className="text-left px-4 py-3">Cliente</th>
                <th className="text-right px-4 py-3 w-40">Líquido</th>
                {temUltima && <th className="text-left px-4 py-3 w-36">Última</th>}
              </tr>
            </thead>
            <tbody data-testid="fatvend-tbody">
              {(!clientes || clientes.length === 0) ? (
                <tr>
                  <td colSpan={temUltima ? 4 : 3} className="py-10 text-center text-muted-foreground">
                    Sem compras no período.
                  </td>
                </tr>
              ) : (
                clientes.map((c) => (
                  <tr key={c.codparc} className="border-t border-black/5 hover:bg-slate-50/50" data-testid={`fatvend-row-${c.codparc}`}>
                    <td className="px-4 py-3 font-medium text-navy tabular-nums">{c.codparc}</td>
                    <td className="px-4 py-3">{c.nome || "—"}</td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums">{brl(c.liquido)}</td>
                    {temUltima && (
                      <td className="px-4 py-3 text-xs text-slate-600 tabular-nums">{fmtDataBR(c.ultima_compra_iso)}</td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
            {(clientes && clientes.length > 0) && (
              <tfoot>
                <tr className="border-t border-black/10 bg-slate-50">
                  <td colSpan={temUltima ? 2 : 2} className="px-4 py-3 text-xs uppercase text-slate-600 font-semibold">
                    Total ({clientes.length} cliente{clientes.length === 1 ? "" : "s"})
                  </td>
                  <td className="px-4 py-3 text-right text-navy font-bold tabular-nums" data-testid="fatvend-total">{brl(totalLiquido)}</td>
                  {temUltima && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <StickyHScrollbar targetRef={scrollerRef} />
    </div>
  );
}
